/**
 * GraphLab — Test page for LiveProjectGraph component.
 *
 * Validates the unified graph component in isolation before
 * replacing existing workspace/spectator implementations.
 *
 * Route: /graph-lab (protected)
 */
import { useEffect, useState } from 'react'
import { useWorkspaceSocket } from '@/hooks/workspace/useWorkspaceSocket'
import { useProjectDataFull } from '@/hooks/workspace/useProjectDataFull'
import { useGraphRefresh } from '@/hooks/workspace/useGraphRefresh'
import { ProjectGraph } from '@/components/workspace/ProjectGraph'
import { projectAPI } from '@/lib/api/projects'
import { Wifi, WifiOff, Loader2 } from 'lucide-react'

interface ProjectListItem {
  id: string
  project_name: string
  database: string
}

export default function GraphLab() {
  const { events, connected, connecting, connect } = useWorkspaceSocket()

  const { project, activities, loading, refreshProject, loadProject } = useProjectDataFull()

  const { selectedProjectId, handleProjectSelect } = useGraphRefresh({
    events,
    refreshProject,
    loadProject,
  })

  const [projects, setProjects] = useState<ProjectListItem[]>([])

  // Auto-connect WebSocket on mount
  useEffect(() => {
    connect()
  }, [connect])

  // Load project list for manual selection
  useEffect(() => {
    projectAPI
      .listProjects({ limit: 20 })
      .then((res) => {
        setProjects(res.data.projects || [])
      })
      .catch(() => {})
  }, [])

  return (
    <div className="h-screen flex flex-col bg-background text-foreground">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <h1 className="text-sm font-semibold">Graph Lab</h1>
          {project && (
            <span className="text-xs text-muted-foreground">
              {project.project_name} ({project.database})
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">Events: {events.length}</span>
          {connecting ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : connected ? (
            <Wifi className="h-4 w-4 text-green-500" />
          ) : (
            <WifiOff className="h-4 w-4 text-red-500" />
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex min-h-0">
        {/* Sidebar: manual project selection */}
        <div className="w-56 border-r border-border p-3 overflow-y-auto shrink-0">
          <h2 className="text-xs font-medium text-muted-foreground mb-2">Projects</h2>
          {projects.length === 0 && <p className="text-xs text-muted-foreground">No projects</p>}
          {projects.map((p) => (
            <button
              key={p.id}
              onClick={() => handleProjectSelect(p.id)}
              className={`w-full text-left px-2 py-1.5 rounded text-xs mb-1 transition-colors ${
                selectedProjectId === p.id
                  ? 'bg-primary/10 text-primary'
                  : 'hover:bg-muted text-foreground'
              }`}
            >
              <div className="font-medium truncate">{p.project_name}</div>
              <div className="text-muted-foreground truncate">{p.database}</div>
            </button>
          ))}
        </div>

        {/* Graph */}
        <div className="flex-1 min-h-0">
          {loading && activities.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : activities.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              Select a project or wait for auto-selection from events
            </div>
          ) : (
            <ProjectGraph
              activities={activities}
              onNodeClick={(id, type) => console.log('[GraphLab] node click:', type, id)}
            />
          )}
        </div>
      </div>
    </div>
  )
}
